import type { SaveData } from '@/core/SaveData';

/**
 * site.ts — the canonical web home (M100b: the owner bought dribbleracer.com).
 *
 * The Cloudflare Pages *.pages.dev origin stays alive forever (it can't be
 * turned off), so without a redirect the game would exist at TWO origins —
 * two localStorage saves, two guest pots, split identity. Production boots
 * therefore bounce legacy hosts to the canonical domain, with ONE deliberate
 * exception: an UN-LINKED save with real progress stays put, because its
 * Skill Points live in that origin's localStorage — the title/results CTAs
 * push them to create a Racer ID, and once linked (career safely on the
 * account) the next visit redirects and sign-in restores everything.
 * Auth landings (?code=) never redirect: the secure exchange must finish on
 * the origin that started it.
 */
export const CANONICAL_ORIGIN = 'https://dribbleracer.com';

/** Hosts that bounce to the canonical domain (production only). */
export const LEGACY_HOSTS = ['dribble-racer.pages.dev'];

/** Where this boot should redirect to, or null to stay. PURE (tested). */
export function legacyRedirectTarget(
  hostname: string,
  href: string,
  save: Pick<SaveData, 'account' | 'lifetimePoints' | 'guestPoints'>,
): string | null {
  if (!LEGACY_HOSTS.includes(hostname)) return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  // A magic-link / OAuth landing finishes where it started (the one-time
  // code only exchanges against this origin's stored verifier).
  if (url.searchParams.has('code') || url.searchParams.has('error_description')) return null;
  // Un-linked progress is trapped in THIS origin's storage — hold the
  // redirect until a Racer ID carries it (the in-game CTAs do the pushing).
  const hasLooseProgress = !save.account && (save.lifetimePoints > 0 || save.guestPoints > 0);
  if (hasLooseProgress) return null;
  return `${CANONICAL_ORIGIN}${url.pathname}${url.search}${url.hash}`;
}
